import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ListIcon from '@mui/icons-material/List';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
const apiUrl = process.env.REACT_APP_API_URL;

const OwnerList = () => {
  const { t, i18n } = useTranslation();
  const [owners, setOwners] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteOwnerId, setDeleteOwnerId] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const token = localStorage.getItem('token');

        const ownersResponse = await axios.get(`${apiUrl}/owners`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setOwners(ownersResponse.data);
      } catch (err) {
        console.error('Failed to fetch owners: ' + err.message);
        setError(t('error_fetching_owners'));
      }
    };

    fetchOwners();
  }, [t]);

  const handleViewClick = (ownerId) => {
    navigate(`/owner-details/${ownerId}`);
  };

  const handleEditClick = (ownerId) => {
    navigate(`/owner-edit/${ownerId}`);
  };

  const handleDeleteClick = (ownerId) => {
    setDeleteOwnerId(ownerId);
    setDeleteConfirmationOpen(true);
  };

  const confirmDelete = async () => {
    if (confirmationText.toLowerCase() !== 'delete') {
      setError(t('please_type_delete_to_confirm'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiUrl}/owners/${deleteOwnerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setOwners(owners.filter((owner) => owner.owner_id !== deleteOwnerId));
      setSuccess(t('owner_deleted_successfully'));
      setDeleteConfirmationOpen(false);
      setConfirmationText('');
    } catch (err) {
      console.error('Failed to delete owner: ' + err.message);
      setError(t('error_deleting_owner'));
    }
  };

  const handleAddOwner = () => {
    navigate('/register-owner');
  };

  const handleCloseDialog = () => {
    setDeleteConfirmationOpen(false);
    setConfirmationText('');
  };
  const filteredOwners = owners.filter((owner) => {
    const fullName = `${owner.first_name || ''} ${
      owner.last_name || ''
    }`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  const handleExportOwnerHorses = async (owner) => {
    try {
      const token = localStorage.getItem('token');
      // جلب الخيول الخاصة بالمالك
      const response = await axios.get(
        `${apiUrl}/owners/${owner.owner_id}/horses-with-details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const horses = response.data;

      if (!Array.isArray(horses) || horses.length === 0) {
        alert('قائمة الخيول غير متوفرة أو غير صحيحة!');
        return;
      }

      // اسماء الأعمدة (الهيدر) حسب اللغة
      const columns = [
        { header: 'Horse Name / اسم الحصان', key: 'name' },
        {
          header: 'Horse Name Arabic / اسم الحصان بالعربية',
          key: 'name_arabic',
        },
        { header: 'Breed / السلالة', key: 'breed' },
        { header: 'Color / اللون', key: 'color' },
        { header: 'Gender / الجنس', key: 'gender' },
        { header: 'Date of Birth / تاريخ الميلاد', key: 'date_of_birth' },
        { header: 'RFID', key: 'rfid_chip_number' },
      ];

      // تجهيز البيانات
      const data = horses.map((h) => ({
        name: h.name || '',
        name_arabic: h.name_arabic || '',
        breed: h.breed_english || h.breed || '',
        color: h.color_english || h.color || '',
        gender:
          h.gender === 'S'
            ? 'Stallion / حصان'
            : h.gender === 'M'
            ? 'Mare / فرس'
            : h.gender || '',
        date_of_birth: h.date_of_birth
          ? new Date(h.date_of_birth).toLocaleDateString('en-GB')
          : '',
        rfid_chip_number: h.rfid_chip_number || '',
      }));

      // بناء الصفوف
      const ws_data = [];

      // الصف الأول: اسم المالك ودمجه من A إلى G
      ws_data.push([
        `Owner: ${owner.first_name || ''} ${owner.last_name || ''} / المالك: ${
          owner.first_name || ''
        } ${owner.last_name || ''}`,
      ]);
      // الصف الثاني: الهيدر
      ws_data.push(columns.map((c) => c.header));
      // بقية الصفوف: بيانات الخيول
      data.forEach((horse) => ws_data.push(columns.map((c) => horse[c.key])));

      // بناء الشيت
      const ws = XLSX.utils.aoa_to_sheet(ws_data);

      // دمج الخلية الأولى من A إلى G للصف الأول
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];

      // تلوين الهيدر (الصف الثاني)
      for (let i = 0; i < columns.length; i++) {
        const cellRef = XLSX.utils.encode_cell({ r: 1, c: i });
        if (!ws[cellRef]) continue;
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '3F51B5' } },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }

      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cell_address]) continue;
          if (!ws[cell_address].s) ws[cell_address].s = {};
          ws[cell_address].s.alignment = {
            horizontal: 'center',
            vertical: 'center',
          };
        }
      }

      // تثبيت عرض الأعمدة (A4)
      ws['!cols'] = [
        { wch: 20 },
        { wch: 28 },
        { wch: 16 },
        { wch: 14 },
        { wch: 12 },
        { wch: 20 },
        { wch: 24 },
      ];

      // إعداد صفحة الطباعة: مقاس A4
      ws['!pageSetup'] = {
        paperSize: 9, // A4
        orientation: 'landscape',
        fitToWidth: 1,
        fitToHeight: 0,
      };

      for (let c = 0; c < columns.length; c++) {
        const cellRef = XLSX.utils.encode_cell({ r: 1, c }); // الصف 2 = r:1
        if (ws[cellRef]) {
          ws[cellRef].s = {
            fill: { fgColor: { rgb: 'F9E7B6' } }, // لون ذهبي فاتح
            font: { bold: true },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: 'CCCCCC' } },
              bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
              left: { style: 'thin', color: { rgb: 'CCCCCC' } },
              right: { style: 'thin', color: { rgb: 'CCCCCC' } },
            },
          };
        }
      }

      // 3. توسيط كل الخلايا
      for (let R = 0; R <= horses.length; R++) {
        for (let C = 0; C < columns.length; C++) {
          const cell = ws[XLSX.utils.encode_cell({ r: R + 1, c: C })];
          if (cell) {
            if (!cell.s) cell.s = {};
            cell.s.alignment = {
              horizontal: 'center',
              vertical: 'center',
              wrapText: true,
            };
          }
        }
      }

      // 4. تنسيق خلية المالك
      if (ws['A1']) {
        ws['A1'].s = {
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
          },
          font: { bold: true, sz: 14 },
          fill: { fgColor: { rgb: 'E2C67F' } },
        };
      }

      // بناء الملف
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Horses');

      // التصدير
      const excelBuffer = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array',
        cellStyles: true,
      });
      saveAs(
        new Blob([excelBuffer], { type: 'application/octet-stream' }),
        `Horses_of_${owner.first_name}_${owner.last_name}.xlsx`
      );
    } catch (err) {
      alert('حدث خطأ أثناء تصدير التقرير');
      console.error(err);
    }
  };

  return (
    <Container
      component="main"
      maxWidth="lg"
      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
    >
      <Box
        sx={{
          marginTop: 2.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        <Typography variant="h4" sx={{ mb: 2.5 }}>
          {t('owner_details')}
        </Typography>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexDirection: i18n.language === 'ar' ? 'row-reverse' : 'row',
            gap: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddOwner}
            startIcon={<AddIcon />}
          >
            {t('add_owner')}
          </Button>

          <TextField
            label={t('search_by_owner_name')}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
          />
        </Box>

        <TableContainer component={Paper} sx={{ marginTop: 2.5 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">#</TableCell>
                <TableCell align="center">{t('owner_name')}</TableCell>
                <TableCell align="center">{t('horses_count')}</TableCell>
                <TableCell align="center">{t('phone')}</TableCell>
                <TableCell align="center">{t('email')}</TableCell>
                <TableCell align="center">{t('address')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOwners.map((owner, index) => (
                <TableRow key={owner.owner_id}>
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: i18n.language === 'ar' ? 2 : 1,
                      }}
                    >
                      <Avatar sx={{ mr: 2 }}>
                        {owner.first_name ? owner.first_name.charAt(0) : '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">
                          {`${owner.first_name || ''} ${owner.last_name || ''}`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{owner.horses_count}</TableCell>
                  <TableCell align="center">{owner.phone || '-'}</TableCell>
                  <TableCell align="center">{owner.email || '-'}</TableCell>
                  <TableCell align="center">{owner.address || '-'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleViewClick(owner.owner_id)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      color="secondary"
                      onClick={() => handleEditClick(owner.owner_id)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(owner.owner_id)}
                    >
                      <DeleteIcon />
                    </IconButton>

                    <IconButton
                      color="info"
                      onClick={() =>
                        navigate(`/OwnerHorsesList/${owner.owner_id}`)
                      }
                    >
                      <ListIcon />
                    </IconButton>
                    <IconButton
                      color="success"
                      onClick={() => handleExportOwnerHorses(owner)}
                      title={t('export_owner_horses_excel')}
                    >
                      <FileDownloadIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={deleteConfirmationOpen}
          onClose={handleCloseDialog}
          aria-labelledby="delete-dialog-title"
        >
          <DialogTitle id="delete-dialog-title">
            {t('confirm_deletion')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('please_type_delete_to_confirm')}
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="confirmationText"
              label={t('type_delete')}
              fullWidth
              variant="standard"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              {t('cancel')}
            </Button>
            <Button onClick={confirmDelete} color="error">
              {t('delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default OwnerList;
